// InsertClarifyQA.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { MathJaxContext } from 'better-react-mathjax';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { renderClarifyBlocks } from '../components/renderClarifyBlocks';
import { renderBlocks } from '../components/renderBlocks';


const mathJaxConfig = {
  loader: { load: ['input/tex', 'output/chtml'] },
  tex: {
    inlineMath: [['$', '$'], ['\\(', '\\)']],
    displayMath: [['\\[', '\\]']]
  }
};
export default function InsertClarifyQA() {
  const [jsonText, setJsonText] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [newGroup, setNewGroup] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [status, setStatus] = useState('');
  const [previewConfirmed, setPreviewConfirmed] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      const { data, error } = await supabase.from('clarify_groups').select('*').order('group_title');
      if (!error && data) setGroups(data);
    };
    const fetchTopics = async () => {
      const { data, error } = await supabase.from('clarify_topics').select('*').order('name');
      if (!error && data) setTopics(data);
    };

    fetchGroups();
    fetchTopics();
  }, []);

  const handleCreateGroup = async () => {
    if (!newGroup.trim()) return;

    const { data: existingGroup, error: fetchError } = await supabase
      .from('clarify_groups')
      .select('id')
      .eq('group_title', newGroup.trim());

    if (fetchError) {
      console.error(fetchError);
      setStatus('❌ Error checking for existing group.');
      await Swal.fire('❌ Error', 'Could not check for existing group.', 'error');
      return;
    }

    if (existingGroup.length > 0) {
      setStatus('⚠️ Group already exists.');
      await Swal.fire('⚠️ Duplicate', 'A group with this name already exists.', 'info');
      return;
    }

    const { data, error } = await supabase
      .from('clarify_groups')
      .insert([{ group_title: newGroup.trim() }])
      .select();

    if (!error && data.length > 0) {
      setGroups((prev) => [...prev, ...data]);
      setSelectedGroup({ id: data[0].id, name: data[0].group_title });
      setNewGroup('');
      setStatus('✅ Group created!');
      await Swal.fire('✅ Success', 'Group created successfully!', 'success');
    } else {
      console.error(error);
      setStatus('❌ Failed to create group.');
      await Swal.fire('❌ Error', 'Group creation failed.', 'error');
    }
  };

  const handleCreateTopic = async () => {
    if (!newTopic.trim()) return;

    const { data: existingTopic, error: fetchError } = await supabase
      .from('clarify_topics')
      .select('id')
      .eq('name', newTopic.trim());

    if (fetchError) {
      console.error(fetchError);
      setStatus('❌ Error checking for existing topic.');
      await Swal.fire('❌ Error', 'Could not check for existing topic.', 'error');
      return;
    }

    if (existingTopic.length > 0) {
      setStatus('⚠️ Topic already exists.');
      await Swal.fire('⚠️ Duplicate', 'A topic with this name already exists.', 'info');
      return;
    }

    const { data, error } = await supabase
      .from('clarify_topics')
      .insert([{ name: newTopic.trim() }])
      .select();

    if (!error && data.length > 0) {
      setTopics((prev) => [...prev, ...data]);
      setSelectedTopic({ id: data[0].id, name: data[0].name });
      setNewTopic('');
      setStatus('✅ Topic created!');
      await Swal.fire('✅ Success', 'Topic created successfully!', 'success');
    } else {
      console.error(error);
      setStatus('❌ Failed to create topic.');
      await Swal.fire('❌ Error', 'Topic creation failed.', 'error');
    }
  };

  const handleParse = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('Expected an array');
      const valid = parsed.every(item => item.order_index && item.title && item.content_blocks);
      if (!valid) throw new Error('Missing fields in one or more entries.');
      setParsedItems(parsed);
      setStatus(`✅ Validated ${parsed.length} item(s).`);
      setPreviewConfirmed(false);
    } catch (err) {
      setParsedItems([]);
      setStatus('❌ Invalid JSON format or missing fields.');
      setPreviewConfirmed(false);
    }
  };

  const handleConfirmPreview = () => {
    if (!selectedTopic || parsedItems.length === 0) {
      setStatus('❌ Select a topic and provide valid JSON.');
      return;
    }
    setPreviewConfirmed(true);
    setStatus('✅ Ready to insert.');
  };

  const handleInsert = async () => {
    let inserted = 0;

    for (const item of parsedItems) {
      const { data: existing } = await supabase
        .from('clarify_entries')
        .select('id')
        .eq('clarify_topic_id', selectedTopic.id)
        .eq('order_index', item.order_index);

      if (existing && existing.length > 0) {
        const { isConfirmed } = await Swal.fire({
          title: 'Overwrite?',
          text: `Entry exists for order ${item.order_index}. Overwrite?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes',
        });
        if (!isConfirmed) continue;

        await supabase
          .from('clarify_entries')
          .delete()
          .eq('clarify_topic_id', selectedTopic.id)
          .eq('order_index', item.order_index);
      }

      const { error } = await supabase.from('clarify_entries').insert([{
        clarify_topic_id: selectedTopic.id,
        clarify_group_id: selectedGroup?.id || null,
        order_index: item.order_index,
        title: item.title,
        content_blocks: item.content_blocks
      }]);

      if (!error) inserted++;
    }

    setStatus(`✅ Inserted ${inserted} items`);
    await Swal.fire('✅ Inserted', `${inserted} entries inserted`, 'success');
    setJsonText('');
    setParsedItems([]);
    setPreviewConfirmed(false);
  };

  const handleFetch = async () => {
    if (!selectedTopic) {
      Swal.fire('⚠️ Please select a topic to fetch');
      return;
    }

    const { data, error } = await supabase
      .from('clarify_entries')
      .select('order_index, title, content_blocks')
      .eq('clarify_topic_id', selectedTopic.id)
      .order('order_index');

    if (!error && data) {
      setJsonText(JSON.stringify(data, null, 2));
      setParsedItems(data);
      setPreviewConfirmed(true);
      setStatus(`✅ Loaded ${data.length} entry(ies)`);
    } else {
      setStatus('❌ Failed to load entries.');
    }
  };

  const handleDelete = async () => {
    if (!selectedTopic) {
      Swal.fire('⚠️ Please select a topic to delete from');
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: 'Delete all entries?',
      text: 'Are you sure you want to delete all entries in this topic?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete all',
    });

    if (!isConfirmed) return;

    const { error } = await supabase
      .from('clarify_entries')
      .delete()
      .eq('clarify_topic_id', selectedTopic.id);

    if (!error) {
      Swal.fire('✅ Deleted all entries');
      setJsonText('');
      setParsedItems([]);
      setPreviewConfirmed(false);
      setStatus('✅ All entries deleted');
    } else {
      Swal.fire('❌ Deletion failed');
    }
  };

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-xl font-bold text-indigo-700 mb-4">📌 Insert Clarification Content</h2>

        <div className="mb-3">
          <label className="block font-medium">Create New Group:</label>
          <div className="flex gap-2">
            <input
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              className="border px-3 py-1 rounded w-full"
              placeholder="Group name"
            />
            <button onClick={handleCreateGroup} className="bg-indigo-500 text-white px-4 py-1 rounded">Add</button>
          </div>
        </div>

        <div className="mb-3">
          <label className="block font-medium">Create New Topic:</label>
          <div className="flex gap-2">
            <input
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              className="border px-3 py-1 rounded w-full"
              placeholder="Topic name"
            />
            <button onClick={handleCreateTopic} className="bg-indigo-500 text-white px-4 py-1 rounded">Add</button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm mb-1">Select Group (Optional)</label>
            <Select
              options={groups.map(g => ({ value: g.id, label: g.group_title }))}
              value={selectedGroup ? { value: selectedGroup.id, label: selectedGroup.name } : null}
              onChange={(opt) => setSelectedGroup(opt ? { id: opt.value, name: opt.label } : null)}
              placeholder="Optional"
              isClearable
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Select Topic</label>
            <Select
              options={topics.map(t => ({ value: t.id, label: t.name }))}
              value={selectedTopic ? { value: selectedTopic.id, label: selectedTopic.name } : null}
              onChange={(opt) => setSelectedTopic(opt ? { id: opt.value, name: opt.label } : null)}
              placeholder="Required"
            />
          </div>
        </div>

        <div className="mt-4 flex gap-3">
          <button onClick={handleFetch} className="bg-purple-600 text-white px-4 py-1 rounded">Fetch</button>
          <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-1 rounded">Delete All</button>
        </div>

        <div className="mt-4">
          <label className="block font-medium mb-1">Paste JSON:</label>
          <textarea
            rows={10}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="w-full border px-3 py-2 font-mono rounded text-sm"
            placeholder='[{"order_index":1,"title":"Concept","content_blocks":[{"type":"text","value":"..."},{"type":"image","value":"..."}]}]'
          />
          <div className="flex gap-4 mt-2">
            <button onClick={handleParse} className="bg-blue-600 text-white px-4 py-1 rounded">Validate</button>
            {parsedItems.length > 0 && (
              <button onClick={handleConfirmPreview} className="bg-yellow-500 text-white px-4 py-1 rounded">Confirm Preview</button>
            )}
          </div>
          {status && <p className="mt-2 text-sm text-indigo-700">{status}</p>}
        </div>

        {previewConfirmed && (
          <button onClick={handleInsert} className="mt-4 bg-green-600 text-white px-5 py-2 rounded hover:bg-green-700">
            🚀 Insert {parsedItems.length} entries
          </button>
        )}

        {parsedItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">🔍 Preview</h3>
            <ul className="space-y-4">
              {parsedItems.map((item, i) => (
                <li key={i} className="border border-gray-200 rounded p-4 bg-white">
                  <p className="font-semibold text-indigo-700">#{item.order_index}: {item.title}</p>
                  <div className="mt-2 text-sm">{renderClarifyBlocks(item.content_blocks)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
}
